import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

type PdfPageProxy = {
  getViewport: (options: { scale: number }) => { width: number; height: number };
  render: (options: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void> };
  cleanup?: () => void;
};

type PdfDocumentProxy = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPageProxy>;
  cleanup?: () => void;
  destroy?: () => void;
};

type PdfLoadingTask = {
  promise: Promise<PdfDocumentProxy>;
  destroy?: () => void;
};

type PdfJsModule = {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  getDocument: (source: { data: Uint8Array }) => PdfLoadingTask;
};

let pdfJsModulePromise: Promise<PdfJsModule> | null = null;
let pdfJsWorkerPromise: Promise<void> | null = null;

async function loadPdfJsModule(): Promise<PdfJsModule> {
  if (!pdfJsModulePromise) {
    pdfJsModulePromise =
      import('pdfjs-dist/legacy/build/pdf') as unknown as Promise<PdfJsModule>;
  }

  if (!pdfJsWorkerPromise) {
    pdfJsWorkerPromise =
      import('pdfjs-dist/legacy/build/pdf.worker.entry').then(() => undefined);
  }

  const [pdfJsModule] = await Promise.all([
    pdfJsModulePromise,
    pdfJsWorkerPromise
  ]);

  return pdfJsModule;
}

@Component({
  selector: 'app-mobile-report-view',
  templateUrl: './mobile-report-view.component.html',
  styleUrls: ['./mobile-report-view.component.css']
})
export class MobileReportViewComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  @Input()
  bookReports: {
    data: SafeHtml | string;
    auxData: SafeHtml[] | string[];
    type: 'pdf' | 'png' | 'html';
    studyUID: string;
  }[] = [];

  @Output() downloadReport = new EventEmitter<void>();

  @ViewChild('reportViewer')
  reportViewer?: ElementRef<HTMLDivElement>;

  reportData: SafeHtml | string = '';
  selectedReportIndex = 0;

  renderedPages: string[] = [];
  renderError = false;
  renderingPreview = false;
  zoomScale = 1;

  private renderRequestId = 0;
  private pinchStartDistance = 0;
  private pinchStartScale = 1;

  private panStartTouch: {
    x: number;
    y: number;
  } | null = null;

  private panStartScroll = {
    left: 0,
    top: 0
  };

  constructor(
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    console.log(
      'MobileReportViewComponent initialized with reports:',
      this.bookReports
    );
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (!changes['bookReports']) {
      return;
    }

    this.reportData = this.bookReports?.[0]?.data ?? '';

    if (this.reportData) {

      this.resetViewerState();

      void this.renderReportPreview(
        this.reportData as string
      );

      return;
    }

    this.cancelRendering();

    this.renderedPages = [];
    this.renderError = false;
    this.renderingPreview = false;

    this.resetZoom();

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.cancelRendering();
  }

  selectReport(index: number): void {

    if (!this.bookReports[index]) {
      return;
    }

    this.selectedReportIndex = index;
    this.reportData = this.bookReports[index].data;

    this.resetViewerState();

    if (this.bookReports[index].type === 'pdf') {
      void this.renderReportPreview(this.reportData as string);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  onViewerTouchStart(event: TouchEvent) {

    event.stopPropagation();

    if (event.touches.length === 1 && this.zoomScale > 1) {

      if (event.cancelable) {
        event.preventDefault();
      }

      this.beginPan(event.touches[0]);
      return;
    }

    if (event.touches.length !== 2) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    this.pinchStartDistance = this.getTouchDistance(
      event.touches[0],
      event.touches[1]
    );

    this.pinchStartScale = this.zoomScale;
  }

  onViewerTouchMove(event: TouchEvent) {

    event.stopPropagation();

    if (event.touches.length === 1 && this.panStartTouch) {

      if (event.cancelable) {
        event.preventDefault();
      }

      const viewer = this.reportViewer?.nativeElement;

      if (!viewer) {
        return;
      }

      const deltaX =
        event.touches[0].clientX - this.panStartTouch.x;

      const deltaY =
        event.touches[0].clientY - this.panStartTouch.y;

      viewer.scrollLeft =
        this.panStartScroll.left - deltaX;

      viewer.scrollTop =
        this.panStartScroll.top - deltaY;

      return;
    }

    if (
      event.touches.length !== 2 ||
      this.pinchStartDistance === 0
    ) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    const nextDistance = this.getTouchDistance(
      event.touches[0],
      event.touches[1]
    );

    if (!nextDistance) {
      return;
    }

    this.zoomScale = this.clampZoom(
      this.pinchStartScale *
      (nextDistance / this.pinchStartDistance)
    );

    //this.cdr.markForCheck();
  }

  onViewerTouchEnd(event: TouchEvent) {

    event.stopPropagation();

    if (
      event.touches.length === 1 &&
      this.zoomScale > 1
    ) {
      this.beginPan(event.touches[0]);
    }
    else if (event.touches.length === 0) {
      this.panStartTouch = null;
    }

    if (event.touches.length < 2) {
      this.pinchStartDistance = 0;
      this.pinchStartScale = this.zoomScale;
    }
  }

  private async renderReportPreview(
    reportURL: string
  ): Promise<void> {

    const requestId = ++this.renderRequestId;

    let loadingTask: PdfLoadingTask | null = null;

    this.renderedPages = [];
    this.renderError = false;
    this.renderingPreview = true;

    this.cdr.markForCheck();

    try {

      const response = await fetch(reportURL);

      const pdfData =
        new Uint8Array(await response.arrayBuffer());

      const pdfJsModule =
        await loadPdfJsModule();

      if (requestId !== this.renderRequestId) {
        return;
      }

      loadingTask = pdfJsModule.getDocument({
        data: pdfData
      });

      const pdfDocument =
        await loadingTask.promise;

      const renderedPages: string[] = [];

      const renderScale =
        Math.max(
          1.5,
          Math.min(
            2,
            window.devicePixelRatio || 1
          )
        );

      for (
        let pageNumber = 1;
        pageNumber <= pdfDocument.numPages;
        pageNumber++
      ) {

        if (requestId !== this.renderRequestId) {
          pdfDocument.destroy?.();
          return;
        }

        const page =
          await pdfDocument.getPage(pageNumber);

        const viewport =
          page.getViewport({
            scale: renderScale
          });

        const canvas =
          document.createElement('canvas');

        const context =
          canvas.getContext('2d');

        if (!context) {
          throw new Error(
            'Canvas context unavailable'
          );
        }

        canvas.width =
          Math.ceil(viewport.width);

        canvas.height =
          Math.ceil(viewport.height);

        await page.render({
          canvasContext: context,
          viewport
        }).promise;

        renderedPages.push(
          canvas.toDataURL(
            'image/jpeg',
            0.92
          )
        );

        page.cleanup?.();
      }

      pdfDocument.cleanup?.();
      pdfDocument.destroy?.();

      if (requestId !== this.renderRequestId) {
        return;
      }

      this.renderedPages = renderedPages;
      this.renderError =
        renderedPages.length === 0;
    }
    catch (error) {

      if (requestId !== this.renderRequestId) {
        return;
      }

      console.error(
        '[ReportViewComponent] Error rendering report preview',
        error
      );

      this.renderedPages = [];
      this.renderError = true;
    }
    finally {

      loadingTask?.destroy?.();

      if (
        requestId === this.renderRequestId
      ) {
        this.renderingPreview = false;
        this.cdr.markForCheck();
      }
    }
  }

  private cancelRendering() {
    this.renderRequestId += 1;
    this.pinchStartDistance = 0;
    this.panStartTouch = null;
  }

  private resetViewerState() {
    this.cancelRendering();

    this.renderedPages = [];
    this.renderError = false;
    this.renderingPreview = false;

    this.resetZoom();
  }

  private resetZoom() {
    this.zoomScale = 1;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
    this.panStartTouch = null;
  }

  private clampZoom(scale: number): number {
    return Math.min(
      4,
      Math.max(1, scale)
    );
  }

  private getTouchDistance(
    firstTouch: Touch,
    secondTouch: Touch
  ): number {

    const deltaX =
      firstTouch.clientX -
      secondTouch.clientX;

    const deltaY =
      firstTouch.clientY -
      secondTouch.clientY;

    return Math.hypot(
      deltaX,
      deltaY
    );
  }

  private beginPan(touch: Touch) {

    const viewer =
      this.reportViewer?.nativeElement;

    if (!viewer) {
      return;
    }

    this.panStartTouch = {
      x: touch.clientX,
      y: touch.clientY
    };

    this.panStartScroll = {
      left: viewer.scrollLeft,
      top: viewer.scrollTop
    };
  }
}